/** Pure helpers that turn a section's flat activity list into the Structure
 *  tab's Zone → Level → Activity tree, plus the untagged group. Grouping uses
 *  each activity's EFFECTIVE placement (committed values with any staged edit
 *  overlaid), so an activity that's been re-coded or dragged to a new
 *  zone/level/section relocates in the tree immediately. No React, no IO.
 */
import { naturalCompare } from "../../../utils/sort";
import type { ActivityCommitted, ActivityPatch, CodeEdit } from "./state";

/** One activity as the Structure tab renders it. */
export interface TreeActivity {
  id: number;
  task_name: string;
  status: string | null;
  takt_work: string;
  takt_prefix: string;
  section_name: string;
  takt_a: string;
  takt_b: string;
  takt_c: string;
  wbs_path: string[];
  start: string | null;
  finish: string | null;
  /** Persisted Takt_SortB — the flattened zone+level rank the board orders by.
   *  Drives zone/level order on load so a saved reorder survives reopening. */
  takt_sort_b?: number | null;
  /** The Takt section this row was fetched under. Untagged WBS-leaf rows have an
   *  empty `section_name`, so this is how they're grouped into their section. */
  loaded_section?: string;
  /** The activity's WBS-node DEFAULT placement (override ?? this). Clearing a
   *  per-activity override snaps the row back to these, so "untag" reverts a
   *  mis-tag to its node's real level instead of going to "no zone". */
  node_section?: string;
  node_takt_a?: string;
  node_takt_b?: string;
  node_takt_c?: string;
}

/** Chronological by start date (ISO strings sort lexically); undated last. */
function byStart(a: TreeActivity, b: TreeActivity): number {
  const av = a.start || "";
  const bv = b.start || "";
  if (!av && !bv) return 0;
  if (!av) return 1;
  if (!bv) return -1;
  return av < bv ? -1 : av > bv ? 1 : 0;
}

export interface TreeLevel {
  takt_c: string;
  activities: TreeActivity[];
}

export interface TreeZone {
  takt_b: string;
  levels: TreeLevel[];
}

export interface SectionTree {
  zones: TreeZone[];
  untagged: TreeActivity[];
}

export type ActivityEdits = Record<number, ActivityPatch>;

/** The committed values for an activity (what a staged patch is diffed against),
 *  resolving a board-wide work-code edit (Work Codes tab) first so a
 *  rename/recolor/recode there is reflected. Shared by the row renderer and the
 *  drag layer so both build identical committed snapshots. */
export function buildCommitted(
  a: TreeActivity,
  codeEdits: Record<string, CodeEdit>,
  colors: Record<string, string>,
): ActivityCommitted {
  const board = codeEdits[`${a.takt_work}|${a.takt_prefix}`];
  return {
    code: board?.newCode ?? a.takt_work,
    name: board?.name ?? a.takt_prefix,
    color: board?.color ?? colors[a.takt_work] ?? "",
    section: a.section_name,
    takt_a: a.takt_a,
    takt_b: a.takt_b,
    takt_c: a.takt_c,
  };
}

/** Which section's tree the activity appears under: a staged move wins; else its
 *  committed Takt_SectionName; else (untagged WBS-leaf rows) the section it was
 *  fetched under, so they show in that section's untagged palette. */
export function effectiveSection(a: TreeActivity, edits: ActivityEdits): string {
  const e = edits[a.id];
  // A staged edit wins; a CLEARED override ("") falls back to the node default
  // (untag reverts to the node's section), not to blank.
  if (e && e.section !== undefined) return e.section || a.node_section || a.loaded_section || "";
  const sn = (a.section_name || "").trim();
  return sn || a.loaded_section || "";
}

// Patch value wins; a cleared one ("") reverts to the node default; untouched
// keeps the loaded effective value.
function resolveDim(patchVal: string | undefined, eff: string, node?: string): string {
  if (patchVal === undefined) return eff;
  return patchVal || node || "";
}

function effectivePlacement(a: TreeActivity, edits: ActivityEdits) {
  const p = edits[a.id];
  return {
    takt_work: p?.code ?? a.takt_work,
    takt_b: resolveDim(p?.takt_b, a.takt_b, a.node_takt_b),
    takt_c: resolveDim(p?.takt_c, a.takt_c, a.node_takt_c),
  };
}

/** An activity is "tagged" (a board bar) once it has a work code AND a zone.
 *  Used for the on-board / needs-code counts. */
export function isTagged(a: { takt_work?: string | null; takt_b?: string | null }): boolean {
  return Boolean((a.takt_work || "").trim() && (a.takt_b || "").trim());
}

/** An activity is "zoned" once it has a Takt_B (its own, or inherited from its
 *  WBS-leaf node mapping). Zoned activities file under their Zone → Level bucket
 *  even with no work code yet — so the section's B/C structure shows and you can
 *  bulk-assign codes to them. Only un-zoned work falls to the untagged rail. */
export function isZoned(a: { takt_b?: string | null }): boolean {
  return Boolean((a.takt_b || "").trim());
}

/** Filter the ACTIVITIES shown (placed + untagged by separate predicates) but
 *  KEEP the full zone/level structure — so during a search you still see every
 *  zone/level (and newly-added empty ones) to drag matches into while tagging.
 *  Which sections appear at all is decided by `treeMatches`. */
export function filterTree(
  tree: SectionTree,
  keepPlaced: (a: TreeActivity) => boolean,
  keepUntagged: (a: TreeActivity) => boolean,
): SectionTree {
  return {
    zones: tree.zones.map((z) => ({
      takt_b: z.takt_b,
      levels: z.levels.map((l) => ({
        takt_c: l.takt_c,
        activities: l.activities.filter(keepPlaced),
      })),
    })),
    untagged: tree.untagged.filter(keepUntagged),
  };
}

/** Every activity in the tree — placed (zone → level) then untagged — as one
 *  flat list. The single traversal shared by the match/collect callers. */
export function treeActivities(tree: SectionTree): TreeActivity[] {
  const out: TreeActivity[] = [];
  for (const z of tree.zones) for (const l of z.levels) for (const a of l.activities) out.push(a);
  for (const a of tree.untagged) out.push(a);
  return out;
}

/** True if any activity (placed or untagged) matches — gates whether a section
 *  shows during a search (a section with zero matches is hidden). Runs per
 *  section on every search keystroke, so it early-returns without allocating. */
export function treeMatches(tree: SectionTree, keep: (a: TreeActivity) => boolean): boolean {
  for (const z of tree.zones) for (const l of z.levels) for (const a of l.activities) if (keep(a)) return true;
  for (const a of tree.untagged) if (keep(a)) return true;
  return false;
}

/** Empty zones/levels the user added that hold no activities yet — merged into
 *  the tree so they render and accept drops. */
export interface SectionExtras {
  zones: string[];
  levels: Record<string, string[]>;
}

/** User-set Takt ordering: zone order for the section, level order per zone. */
export interface SectionOrder {
  zones: string[];
  levels: Record<string, string[]>;
}

/** Saved order first (those that still exist, in order), then the rest as-is. */
function applyOrder(items: string[], saved?: string[]): string[] {
  if (!saved || saved.length === 0) return items;
  const present = new Set(items);
  const savedSet = new Set(saved);
  const out: string[] = [];
  for (const s of saved) if (present.has(s)) out.push(s);
  for (const i of items) if (!savedSet.has(i)) out.push(i);
  return out;
}

/** The Takt A/B/C values actually present in one section — drives the placement
 *  dropdowns in the edit form (scoped to the section, cascading by zone). */
export interface SectionMeta {
  taktAs: string[];
  zones: string[];
  levelsByZone: Record<string, string[]>;
}

/** Build {taktAs, zones, levelsByZone} from a section's activities (effective
 *  placement) + any user-added empty containers. Pure + testable. */
export function sectionMeta(
  activities: TreeActivity[],
  edits: ActivityEdits = {},
  extras?: SectionExtras,
): SectionMeta {
  const taktAs: string[] = [];
  const zones: string[] = [];
  const levelsByZone: Record<string, string[]> = {};
  const seenA = new Set<string>();
  const seenZ = new Set<string>();

  const addLevel = (z: string, c: string) => {
    if (!levelsByZone[z]) levelsByZone[z] = [];
    if (c && levelsByZone[z].indexOf(c) === -1) levelsByZone[z].push(c);
  };
  const addZone = (z: string) => {
    if (z && !seenZ.has(z)) {
      seenZ.add(z);
      zones.push(z);
      if (!levelsByZone[z]) levelsByZone[z] = [];
    }
  };

  for (const a of activities) {
    const p = edits[a.id];
    const ta = ((p && p.takt_a !== undefined ? p.takt_a : a.takt_a) || "").trim();
    const tb = ((p && p.takt_b !== undefined ? p.takt_b : a.takt_b) || "").trim();
    const tc = ((p && p.takt_c !== undefined ? p.takt_c : a.takt_c) || "").trim();
    if (ta && !seenA.has(ta)) {
      seenA.add(ta);
      taktAs.push(ta);
    }
    if (tb) {
      addZone(tb);
      addLevel(tb, tc);
    }
  }

  if (extras) {
    for (const z of extras.zones) addZone(z.trim());
    for (const z of Object.keys(extras.levels)) {
      addZone(z.trim());
      for (const c of extras.levels[z]) addLevel(z.trim(), c.trim());
    }
  }

  return { taktAs, zones, levelsByZone };
}

export function buildSectionTree(
  activities: TreeActivity[],
  edits: ActivityEdits = {},
  extras?: SectionExtras,
  order?: SectionOrder,
): SectionTree {
  const zoneOrder: string[] = [];
  const byZone = new Map<string, Map<string, TreeActivity[]>>();
  const untagged: TreeActivity[] = [];

  const ensureZone = (b: string) => {
    if (!byZone.has(b)) {
      byZone.set(b, new Map());
      zoneOrder.push(b);
    }
    return byZone.get(b)!;
  };

  for (const a of activities) {
    const eff = effectivePlacement(a, edits);
    // File by ZONE, not by tagged-ness: an activity with a zone (its own or
    // inherited from its node) shows under its Zone → Level bucket even with no
    // work code, so the section's B/C structure is visible and codes can be
    // bulk-assigned. Only truly un-zoned work goes to the untagged rail.
    if (!isZoned(eff)) {
      untagged.push(a);
      continue;
    }
    const levels = ensureZone(eff.takt_b.trim());
    const c = eff.takt_c.trim();
    if (!levels.has(c)) levels.set(c, []);
    levels.get(c)!.push(a);
  }

  if (extras) {
    for (const b of extras.zones) ensureZone(b.trim());
    for (const [b, cs] of Object.entries(extras.levels)) {
      const levels = ensureZone(b.trim());
      for (const c of cs) {
        const ct = c.trim();
        if (ct && !levels.has(ct)) levels.set(ct, []);
      }
    }
  }

  // Base zone/level order follows the persisted Takt_SortB (what the board sorts
  // by after a saved reorder). Never-reordered sections (SortB null) fall back to
  // NATURAL order of the zone/level name — LVL1, LVL2, LVL3 — not the arbitrary
  // order rows arrived in. Staged in-session reorders (`order`) still win on top.
  const minSortB = (acts: TreeActivity[]): number => {
    let sb = Infinity;
    for (const a of acts) {
      const v = a.takt_sort_b;
      if (v !== null && v !== undefined && v < sb) sb = v;
    }
    return sb;
  };
  const bySavedRank = (keyed: { key: string; i: number; sb: number }[]): string[] =>
    keyed
      .slice()
      .sort((x, y) => x.sb - y.sb || naturalCompare(x.key, y.key) || x.i - y.i)
      .map((k) => k.key);

  const sortedZoneOrder = bySavedRank(
    zoneOrder.map((b, i) => {
      let sb = Infinity;
      for (const arr of Array.from(byZone.get(b)!.values())) sb = Math.min(sb, minSortB(arr));
      return { key: b, i, sb };
    }),
  );

  const zones: TreeZone[] = applyOrder(sortedZoneOrder, order && order.zones).map((b) => {
    const levels = byZone.get(b)!;
    const sortedLevelKeys = bySavedRank(
      Array.from(levels.keys()).map((c, i) => ({ key: c, i, sb: minSortB(levels.get(c)!) })),
    );
    const levelKeys = applyOrder(
      sortedLevelKeys,
      order && order.levels ? order.levels[b] : undefined,
    );
    return {
      takt_b: b,
      levels: levelKeys.map((takt_c) => ({
        takt_c,
        activities: levels.get(takt_c)!.slice().sort(byStart),
      })),
    };
  });

  return { zones, untagged: untagged.slice().sort(byStart) };
}
